import React, { useState, useEffect, useRef, useCallback } from 'react';
import VideoCard from '../components/VideoCard';
import { youtubeService } from '../services/youtubeService';
import './Home.css';

const trendingQueries = [
  'trending', 'viral', 'popular', 'latest', 'new', 'top',
  'music', 'gaming', 'technology', 'entertainment', 'sports',
  'comedy', 'news', 'education', 'science', 'travel',
  'food', 'fitness', 'lifestyle', 'vlogs', 'reviews'
];

const Home = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [error, setError] = useState(null);
  const observer = useRef();

  const tabs = ['All', 'Music', 'Gaming', 'News', 'Sports', 'Technology', 'Education'];

  const loadVideos = useCallback(async (category = 'All') => {
    setLoading(true);
    setError(null);

    const cacheKey = `videos_${category}`;
    const cachedData = localStorage.getItem(cacheKey);
    const cacheTimestamp = localStorage.getItem(`${cacheKey}_time`);

    if (cachedData && cacheTimestamp) {
      const cacheAge = Date.now() - parseInt(cacheTimestamp);
      if (cacheAge < 30 * 60 * 1000) {
        setVideos(JSON.parse(cachedData));
        setLoading(false);
        return;
      }
    }

    try {
      let fetchedVideos = [];

      if (category === 'All') {
        const popularVideos = await youtubeService.getPopularVideos(24);
        fetchedVideos = popularVideos.items;
        setHasMore(true); // always allow more videos
      } else {
        const categoryVideos = await youtubeService.searchVideos(`${category} trending`, 24);
        fetchedVideos = categoryVideos;
        setHasMore(true);
      }

      localStorage.setItem(cacheKey, JSON.stringify(fetchedVideos));
      localStorage.setItem(`${cacheKey}_time`, Date.now().toString());

      setVideos(fetchedVideos);
    } catch (error) {
      console.error('Error loading videos:', error);
      setError(error.message === 'API_QUOTA_EXCEEDED' ? 'quota' : 'general');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadVideos(activeTab);
  }, [activeTab, loadVideos]);

  const loadMoreVideos = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);

    try {
      const randomQuery = trendingQueries[Math.floor(Math.random() * trendingQueries.length)];
      const moreVideos = await youtubeService.searchVideos(randomQuery, 12);

      const newVideos = moreVideos.filter(video =>
        !videos.some(v => (v.id.videoId || v.id) === (video.id.videoId || video.id))
      );

      setVideos(prev => [...prev, ...newVideos]);
      setHasMore(newVideos.length > 0);
    } catch (error) {
      console.error('Error loading more videos:', error);
    }

    setLoadingMore(false);
  }, [loadingMore, videos]);

  const lastVideoRef = useCallback((node) => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreVideos();
      }
    });

    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore, loadMoreVideos]);

  return (
    <div className="home">
      <div className="tabs">
        {tabs.map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading videos...</p>
        </div>
      ) : error === 'quota' ? (
        <div className="error-message" style={{ textAlign: 'center', padding: '60px 20px', maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ marginBottom: '15px', color: '#fff' }}>YouTube API Quota Exceeded</h2>
          <p style={{ color: '#aaa', lineHeight: '1.6', marginBottom: '20px' }}>
            The daily API quota limit has been reached. Please try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '12px 24px', backgroundColor: '#3ea6ff', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
          >
            Retry
          </button>
        </div>
      ) : error === 'general' ? (
        <div className="error-message" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2 style={{ marginBottom: '15px', color: '#fff' }}>Something went wrong</h2>
          <p style={{ color: '#aaa' }}>Unable to load videos. Please try again later.</p>
          <button
            onClick={() => loadVideos(activeTab)}
            style={{ marginTop: '20px', padding: '12px 24px', backgroundColor: '#3ea6ff', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="video-grid">
            {videos.map((video, index) => {
              const isLastVideo = index === videos.length - 1;
              return (
                <div
                  key={video.id.videoId || video.id}
                  ref={isLastVideo ? lastVideoRef : null}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  className="video-grid-item"
                >
                  <VideoCard video={video} />
                </div>
              );
            })}
          </div>

          {loadingMore && (
            <div className="loading-more">
              <div className="spinner"></div>
              <p>Loading more videos...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Home;
